import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaEdit,
  FaEnvelope,
  FaEye,
  FaHome,
  FaInfoCircle,
  FaListAlt,
  FaPhone,
  FaShareAlt,
  FaStickyNote,
  FaUser,
} from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import { classifyPortalLead } from "./leadClassification";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";

function projectOptionLabel(project) {
  return [project.Short_Code, project.Project_Name].filter(Boolean).join(" — ");
}

function toForm(lead) {
  return {
    First_Name: lead?.First_Name || "",
    Last_Name: lead?.Last_Name || "",
    Country_Code: lead?.Country_Code || "+91",
    Mobile_Number: lead?.Mobile_Number || "",
    Alt_Phone: lead?.Alt_Phone || "",
    Email: lead?.Email || "",
    Property_Service: lead?.Property_Service || "",
    Property_Service_Link: lead?.Property_Service_Link || "",
    Tenant_Project_Name: lead?.Tenant_Project_Name || "",
    Property_Location: lead?.Property_Location || "",
    Requirement_Type: lead?.Requirement_Type || "",
    Property_Type_Id: lead?.Property_Type_Id ? String(lead.Property_Type_Id) : "",
    Budget_Min: lead?.Budget_Min ?? "",
    Budget_Max: lead?.Budget_Max ?? "",
    Preferred_Location: lead?.Preferred_Location || "",
    Lead_Status_Id: lead?.Lead_Status_Id ? String(lead.Lead_Status_Id) : "",
    Site_Visit: !!lead?.Site_Visit,
    Notes: lead?.Notes || "",
  };
}

function buildPayload(form) {
  const countryCode = (() => {
    const raw = String(form.Country_Code || "+91").trim();
    if (!raw) return "+91";
    return raw.startsWith("+") ? raw : `+${raw}`;
  })();

  return {
    First_Name: form.First_Name.trim(),
    Last_Name: form.Last_Name.trim() || null,
    Country_Code: countryCode,
    Mobile_Number: form.Mobile_Number.trim(),
    Alt_Phone: form.Alt_Phone.trim() || null,
    Email: form.Email.trim() || null,
    Property_Service: form.Property_Service.trim() || null,
    Property_Service_Link: form.Property_Service_Link.trim() || null,
    Tenant_Project_Name: form.Tenant_Project_Name.trim() || null,
    Property_Location: form.Property_Location.trim() || null,
    Requirement_Type: form.Requirement_Type || null,
    Property_Type_Id: form.Property_Type_Id || null,
    Budget_Min: form.Budget_Min !== "" ? form.Budget_Min : null,
    Budget_Max: form.Budget_Max !== "" ? form.Budget_Max : null,
    Preferred_Location: form.Preferred_Location.trim() || null,
    Lead_Status_Id: form.Lead_Status_Id ? Number(form.Lead_Status_Id) : null,
    Site_Visit: !!form.Site_Visit,
    Notes: form.Notes.trim() || null,
  };
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function normalizedCountryCode(lead) {
  const raw = String(lead?.Country_Code || "").trim();
  if (!raw) return "";
  return raw.startsWith("+") ? raw : `+${raw}`;
}

function fullName(lead) {
  return `${(lead?.First_Name || "").trim()} ${(lead?.Last_Name || "").trim()}`.trim() || "—";
}

function phone(lead) {
  const cc = normalizedCountryCode(lead);
  return lead?.Mobile_Number ? `${cc ? `${cc} ` : ""}${lead.Mobile_Number}` : "—";
}

function telHref(lead) {
  const raw = `${normalizedCountryCode(lead)}${lead?.Mobile_Number || ""}`.replace(/[^\d+]/g, "");
  if (!raw) return null;
  return `tel:${raw.startsWith("+") ? raw : `+${raw}`}`;
}

function altTelHref(lead) {
  const raw = String(lead?.Alt_Phone || "").replace(/[^\d+]/g, "");
  if (!raw) return null;
  return `tel:${raw.startsWith("+") ? raw : `+${raw}`}`;
}

function budgetRange(lead) {
  if (!lead?.Budget_Min && !lead?.Budget_Max) return "—";
  return `${lead.Budget_Min || "—"} – ${lead.Budget_Max || "—"}`;
}

function backLabelForPath(path) {
  if (path === "/portal/customers") return "Back to customers";
  if (path === "/portal/clients") return "Back to clients";
  return "Back to leads";
}

function initials(lead) {
  const first = (lead?.First_Name || "").trim().charAt(0);
  const last = (lead?.Last_Name || "").trim().charAt(0);
  return `${first}${last}`.toUpperCase() || "L";
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active" || s === "converted" || s === "closed won") {
    return "cportal-badge cportal-badge--success";
  }
  if (s === "inactive" || s === "lost" || s === "closed lost") {
    return "cportal-badge cportal-badge--danger";
  }
  return "cportal-badge";
}

function sharePropertyLink(lead) {
  const url = String(lead?.Property_Service_Link || "").trim();
  if (!url) return;
  const title = String(lead?.Property_Service || "").trim() || "Property link";

  (async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        alert("Property link copied to clipboard.");
        return;
      }
      window.prompt("Copy this link:", url);
    } catch (_) {
      /* user cancelled */
    }
  })();
}

function backPathForMode(mode) {
  if (mode === "customers") return "/portal/customers";
  if (mode === "clients") return "/portal/clients";
  return "/portal/leads";
}

function DetailItem({ label, value, children }) {
  return (
    <div className="cportal-profile-view-item">
      <span className="cportal-profile-view-label">{label}</span>
      <span className="cportal-profile-view-value">{children ?? displayValue(value)}</span>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children, className = "" }) {
  return (
    <section className={`cportal-card cportal-detail-section ${className}`.trim()}>
      <h2 className="cportal-detail-section-title">
        {Icon ? <Icon className="cportal-detail-section-icon" aria-hidden="true" /> : null}
        {title}
      </h2>
      {children}
    </section>
  );
}

function CustomerLeadView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [requirementTypes, setRequirementTypes] = useState(["Buy", "Rent", "Lease"]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [tenantProjects, setTenantProjects] = useState([]);
  const [countries, setCountries] = useState([]);

  const phoneCountryOptions = useMemo(() => {
    const mapped = (countries || [])
      .map((c) => ({
        code: String(c.country_code || c.country_short_code || c.Country_Code || "").trim(),
      }))
      .filter((x) => x.code);
    const codes = new Set(["+91"]);
    mapped.forEach((c) => {
      const code = c.code.startsWith("+") ? c.code : `+${c.code}`;
      codes.add(code);
    });
    return Array.from(codes).sort((a, b) => a.localeCompare(b));
  }, [countries]);

  const backPath = useMemo(() => {
    if (location.state?.from) return backPathForMode(location.state.from);
    if (lead) return backPathForMode(classifyPortalLead(lead));
    return "/portal/leads";
  }, [location.state, lead]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axiosInstance.get(`/client/leads/${id}`);
        if (!active) return;
        setLead(res.data?.data || null);
        setForm(toForm(res.data?.data));
        if (!res.data?.data) setError("Lead not found.");
      } catch (err) {
        if (!active) return;
        setError(err.response?.data?.message || "Could not load lead details.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (location.state?.edit) {
      setEditing(true);
    }
  }, [location.state?.edit]);

  useEffect(() => {
    if (!editing) return;
    let active = true;
    (async () => {
      try {
        const [metaRes, countriesRes] = await Promise.all([
          axiosInstance.get("/client/lead-meta"),
          axiosInstance.get("/client/countries").catch(() => ({ data: { data: [] } })),
        ]);
        if (!active) return;
        const data = metaRes.data?.data || {};
        setPropertyTypes(Array.isArray(data.propertyTypes) ? data.propertyTypes : []);
        if (Array.isArray(data.requirementTypes) && data.requirementTypes.length) {
          setRequirementTypes(data.requirementTypes);
        }
        setLeadStatuses(Array.isArray(data.leadStatuses) ? data.leadStatuses : []);
        setTenantProjects(Array.isArray(data.tenantProjects) ? data.tenantProjects : []);
        const rawCountries = Array.isArray(countriesRes?.data?.data)
          ? countriesRes.data.data
          : Array.isArray(countriesRes?.data)
            ? countriesRes.data
            : [];
        setCountries(rawCountries);
      } catch (_) {
        // Meta is optional; form still works with defaults.
      }
    })();
    return () => {
      active = false;
    };
  }, [editing]);

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.First_Name.trim()) {
      setMessage({ type: "error", text: "First name is required." });
      return;
    }
    const mobileError = mobileValidationMessage(form.Mobile_Number, { required: true });
    if (mobileError) {
      setMessage({ type: "error", text: mobileError });
      return;
    }
    const altPhoneError = mobileValidationMessage(form.Alt_Phone, { label: "Alternate phone" });
    if (altPhoneError) {
      setMessage({ type: "error", text: altPhoneError });
      return;
    }

    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const res = await axiosInstance.put(`/client/leads/${id}`, buildPayload(form));
      const data = res.data?.data;
      if (data) {
        setLead(data);
        setForm(toForm(data));
      }
      setEditing(false);
      setMessage({ type: "success", text: "Lead updated." });
    } catch (err) {
      setMessage({
        type: "error",
        text: err.response?.data?.message || "Could not update lead.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="cportal-page">
        <p className="cportal-muted">Loading…</p>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="cportal-page">
        <button type="button" className="cportal-back-link" onClick={() => navigate(backPath)}>
          <FaArrowLeft /> Back
        </button>
        <div className="cportal-alert cportal-alert-error">{error || "Lead not found."}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="cportal-page">
        <button
          type="button"
          className="cportal-back-link"
          onClick={() => {
            setEditing(false);
            setForm(toForm(lead));
            setMessage({ type: "", text: "" });
          }}
        >
          <FaArrowLeft /> Back to lead
        </button>

        <form className="cportal-card" style={{ marginTop: 16 }} onSubmit={handleSave}>
          <div className="cportal-card-head">
            <h2>Edit lead</h2>
          </div>
          {message.text && (
            <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
              {message.text}
            </div>
          )}

          <h3 className="cportal-form-section">Contact information</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>First name <span className="cportal-required">*</span></label>
              <input value={form.First_Name} onChange={(e) => setField("First_Name", e.target.value)} required />
            </div>
            <div className="cportal-field">
              <label>Last name</label>
              <input value={form.Last_Name} onChange={(e) => setField("Last_Name", e.target.value)} />
            </div>
            <div className="cportal-field cportal-field-phone">
              <label>Mobile number <span className="cportal-required">*</span></label>
              <div className="cportal-phone-row">
                <select
                  value={form.Country_Code}
                  onChange={(e) => setField("Country_Code", e.target.value)}
                  aria-label="Country code"
                >
                  {phoneCountryOptions.map((code) => (
                    <option key={code} value={code}>
                      {code}
                    </option>
                  ))}
                </select>
                <input
                  value={form.Mobile_Number}
                  onChange={(e) =>
                    setField("Mobile_Number", sanitizeMobileInput(e.target.value))
                  }
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit number"
                  required
                />
              </div>
            </div>
            <div className="cportal-field">
              <label>Alternate phone</label>
              <input
                value={form.Alt_Phone}
                onChange={(e) => setField("Alt_Phone", sanitizeMobileInput(e.target.value))}
                inputMode="numeric"
                maxLength={10}
                placeholder="10-digit number"
              />
            </div>
            <div className="cportal-field">
              <label>Email</label>
              <input type="email" value={form.Email} onChange={(e) => setField("Email", e.target.value)} />
            </div>
            <div className="cportal-field">
              <label>Lead location</label>
              <input
                value={form.Preferred_Location}
                onChange={(e) => setField("Preferred_Location", e.target.value)}
              />
            </div>
          </div>

          <h3 className="cportal-form-section">Property requirements</h3>
          <div className="cportal-form-grid">
            <div className="cportal-field">
              <label>Service</label>
              <input
                value={form.Property_Service}
                onChange={(e) => setField("Property_Service", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Project</label>
              <select
                value={form.Tenant_Project_Name}
                onChange={(e) => setField("Tenant_Project_Name", e.target.value)}
              >
                <option value="">Select project…</option>
                {tenantProjects.map((project) => (
                  <option key={project.Tenant_Project_Id} value={project.Project_Name}>
                    {projectOptionLabel(project)}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field">
              <label>Property link</label>
              <input
                value={form.Property_Service_Link}
                onChange={(e) => setField("Property_Service_Link", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Property location</label>
              <input
                value={form.Property_Location}
                onChange={(e) => setField("Property_Location", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Requirement type</label>
              <select
                value={form.Requirement_Type}
                onChange={(e) => setField("Requirement_Type", e.target.value)}
              >
                <option value="">Select…</option>
                {requirementTypes.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field">
              <label>Property type</label>
              <select
                value={form.Property_Type_Id}
                onChange={(e) => setField("Property_Type_Id", e.target.value)}
              >
                <option value="">Select…</option>
                {propertyTypes.map((p) => (
                  <option key={p.Property_Type_Id} value={p.Property_Type_Id}>
                    {p.Property_Type_Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field">
              <label>Budget min</label>
              <input
                type="number"
                value={form.Budget_Min}
                onChange={(e) => setField("Budget_Min", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>Budget max</label>
              <input
                type="number"
                value={form.Budget_Max}
                onChange={(e) => setField("Budget_Max", e.target.value)}
              />
            </div>
            <div className="cportal-field">
              <label>
                Status <span className="cportal-required">*</span>
              </label>
              <select
                value={form.Lead_Status_Id}
                onChange={(e) => setField("Lead_Status_Id", e.target.value)}
              >
                <option value="">Select…</option>
                {leadStatuses.map((s) => (
                  <option key={s.Status_Id} value={s.Status_Id}>
                    {s.Status}
                  </option>
                ))}
              </select>
            </div>
            <div className="cportal-field cportal-field-check">
              <label>
                <input
                  type="checkbox"
                  checked={form.Site_Visit}
                  onChange={(e) => setField("Site_Visit", e.target.checked)}
                />
                <span>Site visit</span>
              </label>
            </div>
          </div>

          <h3 className="cportal-form-section">Notes</h3>
          <div className="cportal-field">
            <textarea rows={3} value={form.Notes} onChange={(e) => setField("Notes", e.target.value)} />
          </div>

          <div className="cportal-form-actions">
            <button
              type="button"
              className="cportal-btn cportal-btn-ghost"
              onClick={() => {
                setEditing(false);
                setForm(toForm(lead));
                setMessage({ type: "", text: "" });
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="cportal-btn cportal-btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    );
  }

  const followups = Array.isArray(lead.followups) ? lead.followups : [];

  return (
    <div className="cportal-page">
      <button type="button" className="cportal-back-link" onClick={() => navigate(backPath)}>
        <FaArrowLeft /> {backLabelForPath(backPath)}
      </button>

      {message.text && (
        <div className={`cportal-alert cportal-alert-${message.type === "error" ? "error" : "success"}`}>
          {message.text}
        </div>
      )}

      <div className="cportal-entity-hero">
        <div className="cportal-entity-hero-left">
          <div className="cportal-entity-avatar" aria-hidden="true">
            {initials(lead)}
          </div>
          <div className="cportal-entity-hero-meta">
            <h1 className="cportal-entity-hero-name">{fullName(lead)}</h1>
            <span className={statusBadgeClass(lead.Lead_Status)}>
              {lead.Lead_Status || "No status"}
            </span>
          </div>
        </div>
        <div className="cportal-entity-hero-actions">
          <button
            type="button"
            className="cportal-btn cportal-btn-ghost"
            onClick={() => setEditing(true)}
          >
            <FaEdit aria-hidden="true" /> Edit
          </button>
        </div>
      </div>

      <div className="cportal-detail-grid">
        <DetailSection icon={FaUser} title="Contact information">
          <div className="cportal-profile-view-grid">
            <DetailItem label="First name" value={lead.First_Name} />
            <DetailItem label="Last name" value={lead.Last_Name} />
            <DetailItem label="Mobile" value={phone(lead)}>
              {lead.Mobile_Number && telHref(lead) ? (
                <a href={telHref(lead)} className="cportal-contact-link">
                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                  {phone(lead)}
                </a>
              ) : (
                displayValue(phone(lead))
              )}
            </DetailItem>
            <DetailItem label="Alternate phone" value={lead.Alt_Phone}>
              {lead.Alt_Phone && altTelHref(lead) ? (
                <a href={altTelHref(lead)} className="cportal-contact-link">
                  {lead.Alt_Phone}
                </a>
              ) : (
                displayValue(lead.Alt_Phone)
              )}
            </DetailItem>
            <DetailItem label="Email" value={lead.Email}>
              {lead.Email ? (
                <a href={`mailto:${lead.Email}`} className="cportal-contact-link">
                  <FaEnvelope size={12} />
                  {lead.Email}
                </a>
              ) : (
                "—"
              )}
            </DetailItem>
            <DetailItem label="Lead location" value={lead.Preferred_Location} />
          </div>
        </DetailSection>

        <DetailSection icon={FaHome} title="Property requirements">
          <div className="cportal-profile-view-grid">
            <DetailItem label="Service" value={lead.Property_Service}>
              <div className="cportal-property-service-row">
                <span>{displayValue(lead.Property_Service)}</span>
                {lead.Property_Service_Link && (
                  <span className="cportal-property-link-actions">
                    <a
                      href={lead.Property_Service_Link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cportal-property-link-btn cportal-property-link-btn--view"
                    >
                      <FaEye size={12} />
                      View
                    </a>
                    <button
                      type="button"
                      className="cportal-property-link-btn cportal-property-link-btn--share"
                      onClick={() => sharePropertyLink(lead)}
                    >
                      <FaShareAlt size={12} />
                      Share
                    </button>
                  </span>
                )}
              </div>
            </DetailItem>
            <DetailItem label="Project" value={lead.Tenant_Project_Name} />
            <DetailItem label="Property location" value={lead.Property_Location} />
            <DetailItem label="Property type" value={lead.Property_Type_Name} />
            <DetailItem label="Requirement type" value={lead.Requirement_Type} />
            <DetailItem label="Budget range" value={budgetRange(lead)} />
            <DetailItem label="Site visit" value={lead.Site_Visit} />
          </div>
        </DetailSection>
      </div>

      <DetailSection icon={FaInfoCircle} title="Other details">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Status" value={lead.Lead_Status} />
          <DetailItem label="Source" value={lead.Source_Name} />
          <DetailItem label="Opportunity" value={lead.Is_Opportunity} />
          <DetailItem label="Automation" value={lead.Automation_Enabled} />
          <DetailItem label="Created on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(lead.Created_On)}
            </span>
          </DetailItem>
          <DetailItem label="Updated on">
            <span className="cportal-datetime-value">
              <FaCalendarAlt aria-hidden="true" />
              {formatDateTime(lead.Updated_On)}
            </span>
          </DetailItem>
        </div>
      </DetailSection>

      <DetailSection icon={FaStickyNote} title="Notes">
        <div className="cportal-profile-view-grid">
          <DetailItem label="Notes" value={lead.Notes} />
        </div>
      </DetailSection>

      <DetailSection icon={FaListAlt} title="Follow-ups">
        <p className="cportal-muted" style={{ margin: "0 0 12px" }}>
          {followups.length} record(s)
        </p>
        {followups.length === 0 ? (
          <p className="cportal-muted">No follow-ups recorded yet.</p>
        ) : (
          <div className="cportal-table-wrap">
            <table className="cportal-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Remarks</th>
                  <th>Next follow-up</th>
                </tr>
              </thead>
              <tbody>
                {followups.map((f) => (
                  <tr key={f.Followup_Id}>
                    <td>{formatDate(f.Followup_Date)}</td>
                    <td>{f.Followup_Type || "—"}</td>
                    <td className="cportal-td-wrap">{f.Remarks || "—"}</td>
                    <td>{formatDate(f.Next_Followup_Date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DetailSection>
    </div>
  );
}

export default CustomerLeadView;
